import { Button, Card, Divider, Row, Space, Tag, Timeline } from "antd";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { ReactNode, useEffect, useMemo } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ProfileOutlined,
  ReloadOutlined,
  StepForwardOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { HistoricalDataFileResponse } from "api/generated";
import {
  FileRule,
  UPLOAD_KIND,
  useHistoricalData,
} from "hooks/historicalData/useHistoricalData";

interface LayoutValidationsStepProps {
  kindSelected: UPLOAD_KIND;
  currentFiles: HistoricalDataFileResponse[];
  validationLogs: string;
  executeValidations: () => void;
  goToPreValidationsStep: () => void;
}

function LayoutValidationsStep({
  kindSelected,
  currentFiles,
  validationLogs,
  executeValidations,
  goToPreValidationsStep,
}: LayoutValidationsStepProps) {
  const {
    FILE_RULES,
    getFileSize,
    processValidationLogs,
    getFileValidations,
    getFileStatus,
  } = useHistoricalData();

  const uploadedFiles: FileRule[] = useMemo(() => {
    return currentFiles
      .map((file) => {
        return FILE_RULES.find((rule) => rule.type === file.type);
      })
      .filter((item) => item !== undefined)
      .filter((rule) => rule.requiredFor.includes(kindSelected));
  }, [currentFiles, kindSelected]);

  const logs = useMemo(
    () => processValidationLogs(validationLogs),
    [validationLogs, processValidationLogs]
  );

  const fileValidations = useMemo(
    () => getFileValidations(validationLogs, uploadedFiles),
    [validationLogs, uploadedFiles, getFileValidations]
  );

  const hasAnyFileError = useMemo(() => {
    return (
      validationLogs.toLowerCase().includes("error") ||
      validationLogs.length === 0
    );
  }, [validationLogs]);

  useEffect(() => {
    if (validationLogs.length === 0) {
      executeValidations();
    }
  }, []);

  function getMetadataByStatus(
    status: "missing" | "error" | "warning" | "valid"
  ): {
    label: string;
    type: string;
    color: string;
    icon: ReactNode;
  } {
    if (hasAnyFileError && status === "valid") {
      return {
        label: "Pendiente de Validación",
        type: "purple",
        color: "purple",
        icon: <ReloadOutlined style={{ color: "purple" }} />,
      };
    }

    switch (status) {
      case "valid":
        return {
          label: "Validado",
          type: "success",
          color: "green",
          icon: <CheckCircleOutlined style={{ color: "green" }} />,
        };

      case "warning":
        return {
          label: "Advertencias",
          type: "warning",
          color: "orange",
          icon: <WarningOutlined style={{ color: "orange" }} />,
        };
      default:
        return {
          label: "Errores",
          type: "error",
          color: "red",
          icon: <CloseCircleOutlined style={{ color: "red" }} />,
        };
    }
  }

  return (
    <div>
      <Divider />

      <Row gutter={24}>
        <ResponsiveCol size="lg">
          <strong>Estado de Validaciones</strong>
        </ResponsiveCol>
        <ResponsiveCol size="lg">
          <Tag color="processing" style={{ float: "right" }}>
            {uploadedFiles.length} archivos cargados
          </Tag>
        </ResponsiveCol>

        {uploadedFiles.map((file, index) => {
          const status = getFileStatus(
            currentFiles,
            fileValidations,
            file.type
          );
          const validations = fileValidations[file.type] || [];

          return (
            <Card
              key={index}
              style={{
                width: "100%",
                borderColor: getMetadataByStatus(status).color,
                margin: "8px",
              }}
            >
              <Row gutter={24}>
                <ResponsiveCol size="lg">
                  <Space direction="vertical">
                    <strong>
                      {getMetadataByStatus(status).icon} {file.type}
                    </strong>
                    {file.description}
                    {getFileSize(currentFiles, file.type)}
                  </Space>
                </ResponsiveCol>
                <ResponsiveCol size="lg">
                  <Tag
                    style={{ float: "right" }}
                    icon={getMetadataByStatus(status).icon}
                    color={getMetadataByStatus(status).type}
                  >
                    {getMetadataByStatus(status).label}
                  </Tag>
                </ResponsiveCol>
                {validations.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {validations.map((val, i) => (
                      <div
                        key={i}
                        style={{
                          color: getMetadataByStatus(val.severity).color,
                          fontSize: 12,
                          marginBottom: 4,
                        }}
                      >
                        {val.severity === "error" ? "❌" : "⚠️"} {val.message}
                      </div>
                    ))}
                  </div>
                )}
              </Row>
            </Card>
          );
        })}
      </Row>
      <Row gutter={24}>
        <Card style={{ width: "100%", borderColor: "gray", margin: "8px" }}>
          <strong
            style={{
              width: "100%",
              gap: "5px",
              display: "flex",
              textWrap: "wrap",
              paddingBottom: "24px",
            }}
          >
            <ProfileOutlined />
            Logs de Validación
          </strong>
          <Timeline items={logs} />
        </Card>
      </Row>
      <Row>
        <ResponsiveCol size="xl">
          <Button
            type="primary"
            style={{ float: "right" }}
            icon={<StepForwardOutlined />}
            disabled={hasAnyFileError}
            onClick={() => goToPreValidationsStep()}
          >
            Ir a revisar el Resumen de prevalidación
          </Button>
        </ResponsiveCol>
      </Row>
    </div>
  );
}

export default LayoutValidationsStep;
